import React from 'react';
import { Tabs, Tab } from '../common/tabs';
import { ArtifactDetails } from './ArtifactDetails';
import { UnifiedStateManager } from './UnifiedStateManager';
import { UnifiedTransitionManager } from './UnifiedTransitionManager';
import { UnifiedProcessManager } from './UnifiedProcessManager';
import { UnifiedPropertiesManager } from './UnifiedPropertiesManager';
import { WorkflowStatusComponent } from '../CompositeCards/ComponentRegistry/WorkflowStatusComponent';
import { ArtifactItemsTab } from './ArtifactItemsTab';
import { StateMachineDiagram } from './StateMachineDiagram';
import { StateMachineValidator } from './StateMachineValidator';
import { StateHistoryViewer } from './StateHistoryViewer';
import { CArtifact } from '../../types/artifacts-branded';
import styles from './ArtifactTabs.module.css';

interface ArtifactTabsProps {
  usePrime: boolean;
  onTogglePrime: (value: boolean) => void;
  artifact: CArtifact | null; // Company artifact
}

export const ArtifactTabs: React.FC<ArtifactTabsProps> = ({
  usePrime,
  onTogglePrime,
  artifact,
}) => {
  const [activeTab, setActiveTab] = React.useState('details');

  // Debug logging - only sample in development to reduce verbosity
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
      console.log('[ArtifactTabs] Artifact received (sampled):', artifact?.id);
    }
  }, [artifact]);

  return (
    <div className={styles.artifactTabsContainer}>
      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${activeTab === 'krybot-agent' ? styles.active : ''}`}
          onClick={() => setActiveTab('krybot-agent')}
        >
          Krybot Agent
        </button>
        <button
          className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={`tab-button ${activeTab === 'states' ? 'active' : ''}`}
          onClick={() => setActiveTab('states')}
        >
          States
        </button>
        <button
          className={`tab-button ${activeTab === 'properties' ? 'active' : ''}`}
          onClick={() => setActiveTab('properties')}
        >
          Properties
        </button>
        <button
          className={`tab-button ${activeTab === 'transitions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transitions')}
        >
          Transitions
        </button>
        <button
          className={`tab-button ${activeTab === 'process-artifacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('process-artifacts')}
        >
          Process Artifacts
        </button>
        <button
          className={`tab-button ${activeTab === 'parameters' ? 'active' : ''}`}
          onClick={() => setActiveTab('parameters')}
        >
          Parameters
        </button>
        {!usePrime && (
          <button
            className={`tab-button ${activeTab === 'workflow' ? 'active' : ''}`}
            onClick={() => setActiveTab('workflow')}
          >
            Workflow
          </button>
        )}
        <button
          className={`tab-button ${activeTab === 'artifact-items' ? 'active' : ''}`}
          onClick={() => setActiveTab('artifact-items')}
        >
          Artifact Items
        </button>
        <button
          className={`tab-button ${activeTab === 'state-machine' ? 'active' : ''}`}
          onClick={() => setActiveTab('state-machine')}
        >
          State Machine
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>
      <div className={styles.tabContent}>
        {activeTab === 'krybot-agent' && (
          <div className={styles.krybotAgentContent}>
            <div style={{ padding: '1rem', color: '#888' }}>
              Krybot Agent integration for company artifacts (to be implemented)
            </div>
          </div>
        )}
        {activeTab === 'details' && (
          <div className={styles.detailsContent}>
            {artifact?.id ? (
              <ArtifactDetails artifactId={artifact.id} onClose={() => {}} />
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to view its details.
              </div>
            )}
          </div>
        )}
        {activeTab === 'states' && (
          <div className={styles.statesContent}>
            {artifact?.id ? (
              <UnifiedStateManager artifactId={artifact.id} usePrime={usePrime} artifact={artifact} />
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to manage its states.
              </div>
            )}
          </div>
        )}
        {activeTab === 'properties' && (
          <div className={styles.propertiesContent}>
            {artifact?.id ? (
              <UnifiedPropertiesManager
                artifactId={artifact.id}
                usePrime={usePrime}
                artifact={artifact}
              />
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to manage its properties.
              </div>
            )}
          </div>
        )}
        {activeTab === 'transitions' && (
          <div className={styles.transitionsContent}>
            {artifact?.id ? (
              <UnifiedTransitionManager
                artifactId={artifact.id}
                usePrime={usePrime}
                artifact={artifact}
              />
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to manage its transitions.
              </div>
            )}
          </div>
        )}
        {activeTab === 'process-artifacts' && (
          <div className={styles.processArtifactsContent}>
            {artifact?.id ? (
              <UnifiedProcessManager
                artifactId={artifact.id}
                usePrime={usePrime}
                artifact={artifact}
              />
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to manage its process artifacts.
              </div>
            )}
          </div>
        )}
        {activeTab === 'parameters' && (
          <div className={styles.parametersContent}>
            {artifact?.id ? (
              <div style={{ padding: '1rem', color: '#888' }}>
                Parameters manager for company artifacts (to be implemented)
              </div>
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to manage its parameters.
              </div>
            )}
          </div>
        )}
        {activeTab === 'workflow' && (
          <div className={styles.workflowContent}>
            {artifact?.id ? (
              <WorkflowStatusComponent
                card={{
                  id: artifact.id,
                  type: 'artifact' as any,
                  data: {
                    artifact: artifact,
                    artifactId: artifact.id
                  } as any,
                  context: {
                    uiRole: 'architect' as any,
                    modelType: 'Company' as any,
                    threadId: 'default',
                    userId: 'current-user',
                    dataSource: 'live' as any
                  },
                  layout: {
                    baseLayout: 'expanded' as any,
                    styling: {
                      backgroundColor: '#fff',
                      borderColor: '#d9d9d9',
                      borderWidth: 1,
                      borderRadius: 8,
                      padding: 16,
                      shadow: 'sm'
                    } as any,
                    sections: []
                  },
                  metadata: {
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    createdBy: 'system',
                    version: 1
                  } as any,
                  location: 'center' as any,
                  triggers: []
                }}
                section={{
                  id: 'workflow-status',
                  type: 'custom' as any,
                  roleVisibility: ['architect'] as any,
                  modelVisibility: ['Company'] as any,
                  component: 'workflow-status',
                  order: 1
                }}
                onAction={async (action, payload) => {
                  console.log('Workflow action:', action, payload);
                }}
              />
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to view its workflow status.
              </div>
            )}
          </div>
        )}
        {activeTab === 'artifact-items' && (
          <div className={styles.artifactItemsContent}>
            {artifact?.id ? (
              <ArtifactItemsTab artifactId={artifact.id} artifact={artifact} />
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to manage its items.
              </div>
            )}
          </div>
        )}
        {activeTab === 'state-machine' && (
          <div className={styles.stateMachineContent}>
            {artifact?.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <StateMachineValidator artifactId={artifact.id} autoValidate={false} />
                <StateMachineDiagram artifactId={artifact.id} />
              </div>
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to view its state machine.
              </div>
            )}
          </div>
        )}
        {activeTab === 'history' && (
          <div className={styles.historyContent}>
            {artifact?.id ? (
              <div style={{ padding: '1rem' }}>
                <div style={{ marginBottom: 16, color: '#666', fontSize: 14 }}>
                  <strong>Note:</strong> State history is tracked per artifact item. To view history for specific items, use the History button in the Artifact Items tab.
                </div>
                <div style={{ padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
                  <p>Artifact-level history view coming soon. Currently, history is tracked at the item level.</p>
                </div>
              </div>
            ) : (
              <div style={{ padding: '1rem', color: '#888' }}>
                Select an artifact to view its history.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
